/**
 * Service worker registration.
 *
 * Registration is manual (`injectRegister: false` in vite.config.ts) so the
 * update experience stays under app control. Rather than importing UI here,
 * this dispatches window CustomEvents that `StatusBanners` listens for.
 *
 * The periodic re-checks below are not optional polish. The browser only looks
 * for a new `sw.js` at registration time and on navigations — an installed PWA
 * can stay alive for days without either, so `onNeedRefresh` never fires and
 * the user is stranded on an old build no matter how correct the prompt-mode
 * setup is.
 */
import { registerSW } from "virtual:pwa-register";

export const PWA_UPDATE_AVAILABLE = "dashbase:pwa-update-available";
export const PWA_OFFLINE_READY = "dashbase:pwa-offline-ready";

/** How often to ask the server whether a new worker exists. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;
/** Don't re-check on every tab focus — throttle foreground checks. */
const FOREGROUND_CHECK_MIN_GAP_MS = 5 * 60 * 1000;

export interface PwaUpdateDetail {
  /** Call with `true` to activate the waiting worker and reload. */
  updateSW: (reloadPage?: boolean) => Promise<void>;
}

export function initPwa(): void {
  if (typeof window === "undefined") return;

  const updateSW = registerSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      let lastCheck = Date.now();
      const check = () => {
        lastCheck = Date.now();
        // Fails harmlessly when offline; the next check catches up.
        registration.update().catch(() => {});
      };

      setInterval(check, UPDATE_CHECK_MS);

      document.addEventListener("visibilitychange", () => {
        if (
          document.visibilityState === "visible" &&
          Date.now() - lastCheck > FOREGROUND_CHECK_MIN_GAP_MS
        ) {
          check();
        }
      });
    },

    onNeedRefresh() {
      window.dispatchEvent(
        new CustomEvent<PwaUpdateDetail>(PWA_UPDATE_AVAILABLE, {
          detail: { updateSW },
        }),
      );
    },

    onOfflineReady() {
      window.dispatchEvent(new CustomEvent(PWA_OFFLINE_READY));
    },

    onRegisterError(error) {
      // A failed registration must not take the app down with it — the app
      // works perfectly well without a service worker.
      console.warn("Service worker registration failed", error);
    },
  });
}
